import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Select, Table, TableProps, Input, Radio } from "antd";
import Spinner from "../../components/shared/Spinner/Spinner";
import {
  QuickOrdersDataType,
  BranchsDataType,
} from "../../interfaces/admin.interface";
import MainButton from "../../components/shared/MainButton/MainButton";
import { QuickOrdersService } from "../../services/quick_orders.service";
import { BranchsService } from "../../services/branchs.service";

const statusOptions = [
  {
    key: "new",
    label: "Əlaqə saxlanılmıyıb",
    value: 1,
  },
  {
    key: "contacted",
    label: "Əlaqə saxlanılıb",
    value: 2,
  },
];

const QuickOrders = () => {
  const [orders, setOrders] = useState<QuickOrdersDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [applyDetails, setApplyDetails] = useState<QuickOrdersDataType | null>(
    null
  );
  const [isStatusModalVisible, setIsStatusModalVisible] =
    useState<boolean>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");
  const [isCompleteModalVisible, setIsCompleteModalVisible] =
    useState<boolean>(false);
  const [selectedOrderForComplete, setSelectedOrderForComplete] =
    useState<QuickOrdersDataType | null>(null);
  const [deliveryType, setDeliveryType] = useState<"branch" | "address">(
    "branch"
  );
  const [branches, setBranches] = useState<BranchsDataType[]>([]);
  const [completeFormData, setCompleteFormData] = useState({
    full_name: "",
    address: "",
    number_prefix: "+994",
    number: "",
    filial_id: null as number | null,
  });
  const { TextArea } = Input;

  const fetchQuickOrdersData = async () => {
    setLoading(true);
    QuickOrdersService.getAllQuickOrders()
      .then((response) => {
        if (Array.isArray(response?.data?.data.data)) {
          const dataWithKeys = response?.data?.data.data?.map(
            (item: QuickOrdersDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setOrders(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchQuickOrdersData();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await BranchsService.getAllBranchs();
      if (Array.isArray(response?.data?.data)) {
        setBranches(response.data.data);
      }
    } catch (error) {
      console.error("Filialları yükləmək alınmadı:", error);
    }
  };

  const handleOpenCompleteModal = (record: QuickOrdersDataType) => {
    setSelectedOrderForComplete(record);
    setDeliveryType("branch");
    // Pre-fill form with existing data if available
    setCompleteFormData({
      full_name: record.name || "",
      address: "",
      number_prefix: "+994",
      number: record.phone || "",
      filial_id: null,
    });
    setIsCompleteModalVisible(true);
  };

  const handleCompleteOrder = async () => {
    if (!selectedOrderForComplete) return;

    // Validation
    if (!selectedOrderForComplete.product || !selectedOrderForComplete.product.id) {
      message.error("Məhsul məlumatı tapılmadı!");
      return;
    }

    if (!completeFormData.full_name.trim()) {
      message.error("Ad Soyad daxil edilməlidir!");
      return;
    }

    if (!completeFormData.number.trim()) {
      message.error("Telefon nömrəsi daxil edilməlidir!");
      return;
    }

    if (deliveryType === "branch" && !completeFormData.filial_id) {
      message.error("Filial seçilməlidir!");
      return;
    }

    if (deliveryType === "address") {
      if (!completeFormData.address.trim()) {
        message.error("Ünvan daxil edilməlidir!");
        return;
      }
    }

    try {
      // Yeni order yarat (bu həm statusu dəyişir, həm də order yaradır)
      const completeResponse = await QuickOrdersService.createOrder({
        full_name: completeFormData.full_name,
        address: deliveryType === "address" ? completeFormData.address : null,
        number_prefix: completeFormData.number_prefix,
        number: completeFormData.number,
        filial_id:
          deliveryType === "branch" ? completeFormData.filial_id : null,
        products: [
          {
            product_id: selectedOrderForComplete.product.id,
            quantity: 1,
          },
        ],
      });

      if (
        completeResponse &&
        (completeResponse.status === 200 ||
          completeResponse.status === 201 ||
          completeResponse.status === 204)
      ) {
        await fetchQuickOrdersData();
        setIsCompleteModalVisible(false);
        setSelectedOrderForComplete(null);
        setDeliveryType("branch");
        setCompleteFormData({
          full_name: "",
          address: "",
          number_prefix: "+994",
          number: "",
          filial_id: null,
        });
        message.success("Sifariş uğurla tamamlandı!");
      } else {
        const errorMessage =
          completeResponse?.data?.message ||
          completeResponse?.data?.error ||
          "Sifarişi tamamlamaq alınmadı.";
        message.error(errorMessage);
        console.error("Order creation error:", completeResponse);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Sifarişi tamamlamaq alınmadı.";
      message.error(errorMessage);
      console.error("Order creation exception:", error);
    }
  };

  const handleDetail = (record: QuickOrdersDataType) => {
    setIsModalVisible(true);
    setApplyDetails(record);
  };

  const handleChangeStatus = (orderId: number, statusId: number) => {
    // Əgər "Əlaqə saxlanılıb" seçilibsə (value: 2), modal aç
    if (statusId === 2) {
      setSelectedOrderId(orderId);
      setNote("");
      setIsStatusModalVisible(true);
    } else {
      // "Əlaqə saxlanılmıyıb" seçilibsə (value: 1), birbaşa API-yə göndər
      handleStatusSubmit(orderId, 1, "");
    }
  };

  const handleStatusSubmit = async (
    orderId: number,
    statusId: number,
    noteText: string
  ) => {
    try {
      const statusName = statusId === 1 ? "new" : "contacted";
      await QuickOrdersService.changeQuickOrderStatus(orderId, {
        status_name: statusName,
        note: noteText || null,
      });

      const updatedOrders = orders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              status: {
                ...order.status,
                id: statusId,
                name: statusName,
              },
              note: noteText || null,
            }
          : order
      );
      setOrders(updatedOrders);
      setIsStatusModalVisible(false);
      setSelectedOrderId(null);
      setNote("");
      message.success("Status uğurla dəyişdirildi!");
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Statusu dəyişdirmək alınmadı."
      );
    }
  };

  const columns: TableProps<QuickOrdersDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => (a.key ?? 0) - (b.key ?? 0),
    },
    {
      title: "Müştəri",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Nömrə",
      dataIndex: "phone",
      render: (_, record) => (
        <a className="content" href={`tel:${record.phone}`}>
          {record.phone}
        </a>
      ),
    },
    {
      title: "Məhsul",
      dataIndex: "product",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record.product?.cover_image && (
            <img
              src={record.product.cover_image}
              alt={record.product.name}
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          )}
          <span>{record.product?.name}</span>
        </div>
      ),
    },
    {
      title: "Məhsul kodu",
      dataIndex: "product",
      render: (_, record) => <p>{record.product?.model_code}</p>,
    },
    {
      title: "Məhsul qiyməti",
      dataIndex: "product",
      render: (_, record) => <p>{record.product?.price} AZN</p>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => {
        return (
          <Select
            value={record.status?.id}
            style={{ width: "200px" }}
            options={statusOptions.map((el) => {
              return {
                label: el.label,
                value: el.value,
              };
            })}
            onChange={(value) => handleChangeStatus(record.id, value)}
          />
        );
      },
    },
    {
      title: "Qeyd",
      dataIndex: "note",
      render: (note) => <p>{note || "-"}</p>,
    },
    {
      title: "Sifariş tarixi",
      dataIndex: "created_at",
      render: (date) => (
        <p>{date ? new Date(date).toLocaleDateString("az-AZ") : "-"}</p>
      ),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Məhsullar",
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="add" onClick={() => handleDetail(record)}>
            <i className="fa-regular fa-eye"></i>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <MainButton
            type="button"
            onClick={() => handleOpenCompleteModal(record)}
          >
            Sifarişi tamamla
          </MainButton>
        </div>
      ),
    },
  ];

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Bir kliklə al</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Bir kliklə al</h3>
          </div>
          <div className="pages_components">
            <div className="pages_table">
              <Table
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={orders}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}
      <Modal
        title={"Məhsul"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {applyDetails?.product && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{ width: "50%", objectFit: "cover" }}
                  src={applyDetails.product.cover_image}
                  alt={applyDetails.product.name}
                />
              </div>
              <p style={{ textAlign: "center" }}>{applyDetails.product.name}</p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={"Əlaqə saxlanılıb"}
        open={isStatusModalVisible}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setSelectedOrderId(null);
          setNote("");
        }}
        onOk={() => {
          if (selectedOrderId !== null) {
            handleStatusSubmit(selectedOrderId, 2, note);
          }
        }}
        okText="Təsdiq et"
        cancelText="Ləğv et"
        style={{ minWidth: "340px" }}
        centered
      >
        <div className="modal_form">
          <div className="form_input">
            <div className="input_wrapper">
              <TextArea
                id="note"
                placeholder="Qeyd daxil edin (məcburi deyil)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                style={{ resize: "none" }}
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={"Sifarişi tamamla"}
        open={isCompleteModalVisible}
        onCancel={() => {
          setIsCompleteModalVisible(false);
          setSelectedOrderForComplete(null);
          setDeliveryType("branch");
          setCompleteFormData({
            full_name: "",
            address: "",
            number_prefix: "+994",
            number: "",
            filial_id: null,
          });
        }}
        onOk={handleCompleteOrder}
        okText="Təsdiq et"
        cancelText="Ləğv et"
        style={{ minWidth: "340px" }}
        centered
        width={600}
      >
        <div className="modal_form" style={{ padding: "0" }}>
          <div className="form_input" style={{ marginBottom: "16px" }}>
            <label
              htmlFor="full_name"
              style={{ marginBottom: "8px", display: "block" }}
            >
              Ad, Soyad *
            </label>
            <Input
              id="full_name"
              placeholder="Ad, Soyad daxil edin"
              value={completeFormData.full_name}
              onChange={(e) =>
                setCompleteFormData({
                  ...completeFormData,
                  full_name: e.target.value,
                })
              }
              style={{ height: "40px" }}
            />
          </div>

          <div className="form_input" style={{ marginBottom: "16px" }}>
            <label style={{ marginBottom: "8px", display: "block" }}>
              Təslimat ünvanı
            </label>
            <Radio.Group
              value={deliveryType}
              onChange={(e) => {
                setDeliveryType(e.target.value);
                if (e.target.value === "branch") {
                  setCompleteFormData({
                    ...completeFormData,
                    address: "",
                  });
                } else {
                  setCompleteFormData({
                    ...completeFormData,
                    filial_id: null,
                  });
                }
              }}
              style={{ width: "100%" }}
            >
              <div style={{ marginBottom: "8px" }}>
                <Radio value="branch">Filialdan təslim al</Radio>
              </div>
              <div>
                <Radio value="address">Ünvan əlavə et</Radio>
              </div>
            </Radio.Group>
          </div>

          {deliveryType === "branch" && (
            <div className="form_input" style={{ marginBottom: "16px" }}>
              <label
                htmlFor="filial_id"
                style={{ marginBottom: "8px", display: "block" }}
              >
                Filial seçin *
              </label>
              <Select
                id="filial_id"
                placeholder="Filial seçin"
                value={completeFormData.filial_id}
                onChange={(value) =>
                  setCompleteFormData({
                    ...completeFormData,
                    filial_id: value,
                  })
                }
                style={{ width: "100%", height: "40px" }}
                options={branches.map((branch) => ({
                  label: branch.name,
                  value: branch.id,
                }))}
                popupMatchSelectWidth={false}
              />
            </div>
          )}

          {deliveryType === "address" && (
            <>
              {/* <div className="form_input" style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="fin_code"
                  style={{ marginBottom: "8px", display: "block" }}
                >
                  FIN kod *
                </label>
                <Input
                  id="fin_code"
                  placeholder="FIN kod daxil edin"
                  value={completeFormData.fin_code}
                  onChange={(e) =>
                    setCompleteFormData({
                      ...completeFormData,
                      fin_code: e.target.value,
                    })
                  }
                  style={{ height: "40px" }}
                />
              </div> */}

              <div className="form_input" style={{ marginBottom: "16px" }}>
                <label
                  htmlFor="address"
                  style={{ marginBottom: "8px", display: "block" }}
                >
                  Ünvan *
                </label>
                <TextArea
                  id="address"
                  placeholder="Ünvan daxil edin"
                  value={completeFormData.address}
                  onChange={(e) =>
                    setCompleteFormData({
                      ...completeFormData,
                      address: e.target.value,
                    })
                  }
                  rows={3}
                  style={{ resize: "none" }}
                />
              </div>
            </>
          )}

          <div className="form_input" style={{ marginBottom: "0" }}>
            <label
              htmlFor="number_prefix"
              style={{ marginBottom: "8px", display: "block" }}
            >
              Nömrə *
            </label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Select
                value={completeFormData.number_prefix}
                onChange={(value) =>
                  setCompleteFormData({
                    ...completeFormData,
                    number_prefix: value,
                  })
                }
                style={{ width: "120px", height: "40px" }}
                options={[{ label: "+994", value: "+994" }]}
              />
              <Input
                id="number"
                placeholder="Telefon nömrəsi"
                value={completeFormData.number}
                onChange={(e) =>
                  setCompleteFormData({
                    ...completeFormData,
                    number: e.target.value,
                  })
                }
                style={{ flex: 1, height: "40px" }}
              />
            </div>
          </div>

          {/* <div className="form_input">
            <label htmlFor="reason">Səbəb</label>
            <div className="input_wrapper">
              <TextArea
                id="reason"
                placeholder="Səbəb daxil edin"
                value={completeFormData.reason}
                onChange={(e) =>
                  setCompleteFormData({
                    ...completeFormData,
                    reason: e.target.value,
                  })
                }
                rows={4}
                style={{ resize: "none" }}
              />
            </div>
          </div> */}
        </div>
      </Modal>
    </div>
  );
};

export default QuickOrders;
